<template>
  <div class="magic-link-auth-form text-center">
    <!-- Loading state -->
    <div v-if="!error">
      <div class="mb-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <p class="text-muted">{{ $t('forms.magic_link.verifying') }}</p>
    </div>

    <!-- Error state -->
    <div v-else class="error-state">
      <div class="mb-4">
        <Icon name="grAlertTriangle" width="48" class="text-danger"/>
      </div>
      <p class="text-danger">{{ errorMessage }}</p>
      <button @click="goToLogin" class="btn btn-primary mt-3">
        {{ $t('forms.magic_link.try_again') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { AuthProviders } from '../../constants/authProviders'

/**
 * Magic Link Auto-Verify Component
 *
 * Automatically verifies magic link code when present in URL query params.
 * Usage: <MagicLinkAuthForm @success="handleSuccess" @error="handleError" />
 *
 * This component is shown when ?magic_link=CODE is detected in URL.
 */
export default {
  name: 'MagicLinkAuthForm',

  data() {
    return {
      error: false,
      errorMessage: ''
    }
  },

  computed: {
    ...mapGetters('auth', ['hasAcceptedRequiredPolicies']),

    /**
     * Get magic_link code from URL query params (8 digits)
     */
    magicLinkCode() {
      return this.$route.query.magic_link
    },

    /**
     * Get email from URL query params
     */
    email() {
      return this.$route.query.email
    },

    /**
     * Get picacode from query params if present
     */
    picacode() {
      return this.$route.query.picacode
    },

    /**
     * Get redirect route name from query params
     */
    redirectRoute() {
      const redirectParam = this.$route.query.redirect || this.$route.query.next
      const redirectMap = {
        'checkins': 'checkins-list',
        'checkins-list': 'checkins-list',
        'homepage': 'homepage'
      }
      return redirectMap[redirectParam] || 'checkins-list'
    },

    /**
     * Final redirect destination
     */
    finalRedirectOptions() {
      if (this.picacode) {
        return {
          name: 'checkins-detail',
          params: { picacode: this.picacode }
        }
      }
      return { name: this.redirectRoute }
    }
  },

  mounted() {
    this.performMagicLinkVerify()
  },

  methods: {
    ...mapActions('auth', ['authenticate']),

    /**
     * Verify magic link code and authenticate
     */
    async performMagicLinkVerify() {
      if (!this.magicLinkCode || !this.email) {
        this.showError(this.$t('forms.magic_link.error_invalid_link'))
        return
      }

      try {
        await this.authenticate({
          method: AuthProviders.MAGIC_LINK,
          credentials: {
            email: this.email,
            code: this.magicLinkCode
          },
          remember: true
        })

        // Handle picacode if present
        if (this.picacode) {
          sessionStorage.setItem('picacode', this.picacode)
        }

        // Check privacy policies
        if (!this.hasAcceptedRequiredPolicies) {
          await this.$router.push({
            name: 'terms',
            params: { next: this.redirectRoute }
          })
        } else {
          await this.$router.push(this.finalRedirectOptions)
        }

        this.$emit('success')

      } catch (error) {
        console.error('Magic link verification failed:', error)
        this.showError(this.$t('forms.magic_link.error_expired'))
        this.$emit('error', error)
      }
    },

    showError(message) {
      this.error = true
      this.errorMessage = message
    },

    goToLogin() {
      // Save email to store so MagicLinkForm can prefill it
      if (this.email) {
        this.$store.commit('auth/SET_CONTEXT_PARAMS', { email: this.email })
      }
      // Clean URL and show normal login form
      this.$router.replace({ name: 'login' })
    }
  }
}
</script>

<style scoped>
.magic-link-auth-form {
  padding: 2rem;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
